import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';

// Helper function to display cigar name without duplicating brand
const formatCigarName = (brand, name) => {
  if (!name || name.toLowerCase() === brand?.toLowerCase()) {
    return brand || 'Unknown';
  }
  return `${brand} ${name}`;
};

const MyCommentsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [comments, setComments] = useState([]);
  const [cigars, setCigars] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchMyComments();
    }
  }, [token]);

  const fetchMyComments = async () => {
    try {
      const res = await axios.get(`${API}/comments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(res.data);
      
      // Fetch cigar details in a single batch request
      const uniqueCigarIds = [...new Set(res.data.map(c => c.cigar_id))];
      
      if (uniqueCigarIds.length > 0) {
        try {
          const batchRes = await axios.post(`${API}/cigars/batch`, uniqueCigarIds, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const cigarDetails = {};
          for (const cigar of batchRes.data) {
            cigarDetails[cigar.id] = cigar;
          }
          setCigars(cigarDetails);
        } catch (err) {
          console.error('Failed to fetch cigars batch');
        }
      }
    } catch (error) {
      console.error('Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${dateStr} ${timeStr}`;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to view your comments</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">My Comments</h1>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : comments.length === 0 ? (
          <div className="text-center text-white/50 py-8">You haven&apos;t made any comments yet.</div>
        ) : (
          comments.map((comment) => {
            const cigar = cigars[comment.cigar_id];
            return (
              <div
                key={comment.id}
                onClick={() => navigate(`/cigar/${comment.cigar_id}/discussions`)}
                className="bg-white/5 rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
              >
                {/* Cigar Info */}
                {cigar && (
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/10">
                    <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                      <CigarImage src={cigar.image_url} alt={cigar.name} />
                    </div>
                    <div>
                      <p className="text-xs text-white/50">{cigar.brand}</p>
                      <p className="text-white font-semibold">{formatCigarName(cigar.brand, cigar.name)}</p>
                    </div>
                  </div>
                )}
                
                {/* Comment Content */}
                <div>
                  <p className="text-white/90 break-words mb-2">{comment.text}</p>
                  <p className="text-white/40 text-sm">{formatDateTime(comment.created_at)}</p>
                </div>
                
                {/* Tap to view indicator */}
                <div className="flex items-center justify-end mt-2 text-amber-500 text-sm">
                  <MessageCircle size={14} className="mr-1" />
                  View Discussion →
                </div>
              </div>
            );
          })
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default MyCommentsPage;
